import clsx from 'clsx'
import React, { useRef } from 'react'
import mergeRefs from 'react-merge-refs'
import s from './Button.module.scss'

export type ButtonOwnProps<E extends React.ElementType = React.ElementType> = {
  /**
   * The HTML element to render
   */
  as?: E
  /**
   * Button Variant
   * */
  variant?: 'primary' | 'secondary' | 'ghost' | 'buy' | 'underline' | 'menu'

  /**
   * How large should the button be?
   */
  size?: 'no-padding' | 'small' | 'medium' | 'large'
  /**
   * Optional click handler
   */
  onClick?: () => void
  /**
   * If the button is in loading state
   */
  loading?: boolean
  /**
   * If the button is disabled
   * */
  disabled?: boolean
  /**
   * Children
   * */
  children: React.ReactNode

  /**
   * Optional class name
   * */
  className?: string
  labelClassName?: string
  icon?: React.ReactNode
}

export type ButtonProps<E extends React.ElementType> = ButtonOwnProps<E> &
  Omit<React.ComponentProps<E>, keyof ButtonOwnProps>

const defaultElement = 'button'
//@ts-ignore
const Button: <E extends React.ElementType = typeof defaultElement>(
  props: ButtonProps<E>
) => React.ReactElement | null = React.forwardRef(function Button(
  props: ButtonOwnProps,
  buttonRef: React.Ref<any>
) {
  const Element = props.as || defaultElement
  const {
    className,
    labelClassName,
    children,
    disabled = false,
    variant = 'primary',
    loading = false,
    size,
    icon,
    as,
    ...rest
  } = props
  const ref = useRef<typeof Element>(null)
  const rootClassName = clsx(
    s.root,
    {
      [s.primary]: variant === 'primary',
      [s.secondary]: variant === 'secondary',
      [s.ghost]: variant === 'ghost',
      [s.buy]: variant === 'buy',
      [s.underline]: variant === 'underline',
      [s.menu]: variant === 'menu',
      [s.noPadding]: size === 'no-padding',
      [s.small]: size === 'small',
      [s.medium]: size === 'medium',
      [s.large]: size === 'large',
      [s.disabled]: disabled,
      [s.loading]: loading,
    },
    className
  )

  return (
    //@ts-ignore
    <Element
      className={rootClassName}
      {...rest}
      disabled={disabled || loading}
      ref={mergeRefs([ref, buttonRef])}
    >
      {loading && (
        <div className={s.spinnerButton}>
          <div className={s.spinner}>
            <span className={s.spinnerSpan}>Loading...</span>
          </div>
        </div>
      )}
      {icon && <span className={s.icon}>{icon}</span>}
      <span
        className={clsx(s.label, labelClassName, {
          [s.underlineAnimation]: variant === 'buy',
        })}
      >
        {children}
      </span>
    </Element>
  )
})

export default Button
